import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Alert,
  Snackbar,
  AlertTitle,
  CircularProgress,
  Avatar,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import { 
  Download as DownloadIcon, 
  Person as PersonIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  School as SchoolIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarTodayIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getCertificadoPlantillaById } from '../../modules/apps/user-management/users-list/core/_requests';
import { PageTitle } from '../../../_metronic/layout/core';
import BackButton from '../../components/BackButton/BackButton';
import axios from 'axios';
import { useAuth } from '../../modules/auth';
import { concatArrayToStringJoinBy } from '../../util/Index';

interface PlantillaPreview {
  id: number;
  nombre_plantilla: string;
  entidad: string;
  texto_certifican: string;
  titulo_certificado: string;
  texto_certificado: string;
  texto_intensidad_horaria: string;
  texto_contexto: string;
  nombre_firmante: string;
  cargo_firmante: string;
  area_firmante: string;
  firma_base64: string | null;
  logos_base64: string[];
  fondo_base64: string;
  color_primario: string;
  color_secundario: string;
  tamano_fuente_titulo: number;
  tamano_fuente_texto: number;
  vigente: boolean;
}

interface UserComplemento {
  primer_nome?: string;
  segundo_nome?: string;
  primer_apelido?: string;
  segundo_apelido?: string;
  numero_doc?: string;
  ciudad?: string;
  correo?: string;
}

interface UserData {
  id: number;
  email: string;
  nombreCompleto: string;
  numeroDocumento?: string;
  ciudad?: string;
  userComplemento?: UserComplemento;
  nick?: string;
  role?: number;
}

const CertificadoPlantillaPreviewPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [plantilla, setPlantilla] = useState<PlantillaPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const { currentUser, auth } = useAuth();
  
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState(true);
  const [alertMsg, setAlertMsg] = useState('');

  useEffect(() => {
    if (id) {
      fetchPlantilla();
      loadUserData();
    }
  }, [id, currentUser, auth]);

  const loadUserData = async () => {
    try {
      setLoadingUser(true);
      console.log('Datos de autenticación (currentUser):', currentUser);
      console.log('Datos de autenticación (auth):', auth);
      
      let userId: number | undefined;
      let userEmail: string | undefined;
      let userNick: string | undefined;
      let userRole: number | undefined;
      
      if (currentUser && currentUser.user) {
        userId = currentUser.user.id;
        userEmail = currentUser.user.email;
        userNick = currentUser.user.nick;
        userRole = currentUser.user.role;
      } else if (auth && auth.user) {
        userId = auth.user.id;
        userEmail = auth.user.email;
        userNick = auth.user.nick;
        userRole = auth.user.role;
      } else {
        try {
          const savedAuth = localStorage.getItem('auth');
          if (savedAuth) {
            const parsedAuth = JSON.parse(savedAuth);
            if (parsedAuth && parsedAuth.user) {
              userId = parsedAuth.user.id;
              userEmail = parsedAuth.user.email;
              userNick = parsedAuth.user.nick;
              userRole = parsedAuth.user.role;
            }
          }
        } catch (error) {
          console.error('Error leyendo localStorage:', error);
        }
      }
      
      if (!userId || !userEmail) {
        setUserData({
          id: 1,
          email: 'usuario@ejemplo.com',
          nombreCompleto: 'Usuario Actual',
          numeroDocumento: '123456789',
          ciudad: 'Bogotá D.C.',
          nick: 'Usuario'
        });
        return;
      }
      
      try {
        const API_URL_OF = process.env.REACT_APP_API_URL;
        const token = auth?.api_token || currentUser?.api_token;
        
        if (!token) {
          throw new Error('Token no disponible');
        }
        
        const response = await axios.get(
          `${API_URL_OF}/user/by-id?id=${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        console.log('Respuesta detallada del usuario:', response.data);
        
        if (response.data && response.data.data) {
          const userDetails = response.data.data;
          const complemento = userDetails.UserComplemento;
          
          let nombreCompleto = userNick || userEmail || 'Usuario';
          let numeroDocumento = 'Sin documento';
          let ciudad = 'Bogotá D.C.';
          
          if (complemento) {
            nombreCompleto = concatArrayToStringJoinBy([
              complemento.primer_nome,
              complemento.segundo_nome,
              complemento.primer_apelido,
              complemento.segundo_apelido
            ], ' ') || nombreCompleto;
            
            numeroDocumento = complemento.numero_doc || numeroDocumento;
            ciudad = complemento.ciudad || ciudad;
          }
          
          setUserData({
            id: userId,
            email: userEmail,
            nombreCompleto,
            numeroDocumento,
            ciudad,
            userComplemento: complemento,
            nick: userNick,
            role: userRole
          });
        } else {
          throw new Error('Estructura de respuesta no reconocida');
        }
        
      } catch (error) {
        console.warn('No se pudieron obtener datos detallados del usuario, usando datos básicos:', error);
        
        setUserData({
          id: userId,
          email: userEmail,
          nombreCompleto: userNick || userEmail || 'Usuario Actual',
          numeroDocumento: 'Documento no disponible',
          ciudad: 'Bogotá D.C.',
          nick: userNick,
          role: userRole
        });
      }
      
    } catch (error) {
      console.error('Error cargando datos del usuario:', error);
      setUserData({
        id: 1,
        email: 'usuario@ejemplo.com',
        nombreCompleto: 'Usuario Actual',
        numeroDocumento: '123456789',
        ciudad: 'Bogotá D.C.',
        nick: 'Usuario'
      });
    } finally {
      setLoadingUser(false);
    }
  };

  const showAlert = (success: boolean, message: string) => {
    setAlert(success);
    setAlertMsg(message);
    setOpen(true);
    setTimeout(() => {
      setOpen(false);
    }, 3000);
  };

  const fetchPlantilla = async () => {
    try {
      setLoading(true);
      const response = await getCertificadoPlantillaById(Number(id));
      
      let plantillaData = null;
      
      if (response && response.data && response.data.data) {
        plantillaData = response.data.data;
      } else if (response && response.data) {
        plantillaData = response.data;
      }
      
      if (plantillaData) {
        console.log('Plantilla cargada para preview:', plantillaData);
        setPlantilla(plantillaData);
      } else {
        throw new Error('Estructura de respuesta no reconocida');
      }
    } catch (error: any) {
      console.error('Error fetching plantilla:', error);
      showAlert(false, 'Error al cargar la plantilla');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSample = async () => {
    if (!id) return;
    
    try {
      setDownloading(true);
      
      let nombreCurso = 'CURSO DE CAPACITACIÓN PROFESIONAL';
      let intensidadHoraria = '40';
      
      const cursoGuardado = localStorage.getItem('certificado_nombre_curso');
      const horasGuardadas = localStorage.getItem('certificado_intensidad_horaria');
      
      if (cursoGuardado) nombreCurso = cursoGuardado;
      if (horasGuardadas) intensidadHoraria = horasGuardadas;
      
      const API_URL_OF = process.env.REACT_APP_API_URL;
      
      let url = `${API_URL_OF}/certificado-plantilla/${id}/muestra`;
      const params = new URLSearchParams();
      if (nombreCurso) params.append('nombreCurso', nombreCurso);
      if (intensidadHoraria) params.append('intensidadHoraria', intensidadHoraria);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const token = auth?.api_token || currentUser?.api_token;
      if (!token) {
        throw new Error('No hay token de autenticación disponible');
      }
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const urlBlob = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlBlob;
      link.download = `muestra-${plantilla?.nombre_plantilla.replace(/\s+/g, '-') || 'certificado'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);
      
      showAlert(true, 'PDF descargado exitosamente');
    } catch (error: any) {
      console.error('Error downloading sample:', error);
      showAlert(false, error.response?.data?.message || 'Error al descargar el PDF de muestra');
    } finally {
      setDownloading(false);
    }
  };

  const getDatosActuales = () => {
    if (!userData) {
      return {
        nombreCompleto: 'Cargando...',
        numeroDocumento: '...',
        nombreCurso: 'CURSO DE CAPACITACIÓN PROFESIONAL',
        intensidadHoraria: '40',
        ciudad: 'Bogotá D.C.',
        dia: new Date().getDate().toString(),
        mes: getMesEnTexto(new Date().getMonth()),
        anio: new Date().getFullYear().toString(),
      };
    }

    const nombreCurso = localStorage.getItem('certificado_nombre_curso') || 'CURSO DE CAPACITACIÓN PROFESIONAL';
    const intensidadHoraria = localStorage.getItem('certificado_intensidad_horaria') || '40';

    return {
      nombreCompleto: userData.nombreCompleto,
      numeroDocumento: userData.numeroDocumento,
      nombreCurso: nombreCurso,
      intensidadHoraria: intensidadHoraria,
      ciudad: userData.ciudad,
      dia: new Date().getDate().toString(),
      mes: getMesEnTexto(new Date().getMonth()),
      anio: new Date().getFullYear().toString(),
    };
  };

  const getMesEnTexto = (mesNumero: number) => {
    const meses = [
      'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];
    return meses[mesNumero] || 'enero';
  };

  const replaceVariables = (text: string) => {
    if (!text) return '';
    const datos = getDatosActuales();
    return text
      .replace(/\{\{nombre\}\}/g, `<span style="color: ${plantilla?.color_secundario || '#0066cc'}; font-weight: bold;">${datos.nombreCompleto}</span>`)
      .replace(/\{\{documento\}\}/g, `<span style="color: ${plantilla?.color_secundario || '#0066cc'}; font-weight: bold;">${datos.numeroDocumento}</span>`)
      .replace(/\{\{curso\}\}/g, `<span style="color: ${plantilla?.color_secundario || '#0066cc'}; font-weight: bold;">${datos.nombreCurso}</span>`)
      .replace(/\{\{horas\}\}/g, `<span style="color: ${plantilla?.color_secundario || '#0066cc'}; font-weight: bold;">${datos.intensidadHoraria}</span>`);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const getInitials = (nombre: string) => {
    if (!nombre) return '??';
    const words = nombre.split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
    }
    return nombre.substring(0, 2).toUpperCase();
  };

  const formatEmail = (email: string) => {
    if (!email) return '';
    if (email.length > 25) {
      return `${email.substring(0, 22)}...`;
    }
    return email;
  };

  const getRoleName = (roleId?: number) => {
    if (!roleId) return 'Usuario';
    
    const roles: { [key: number]: string } = {
      1: 'Admin',
      2: 'Empleador',
      3: 'Candidato',
      4: 'Consejero Empresarial',
      5: 'Consejero Candidato',
    };
    
    return roles[roleId] || `Rol ${roleId}`;
  };

  if (loading || loadingUser) {
    return (
      <div className='p-4 shadow-4 rounded-3 mt-15' style={{ backgroundColor: '#FFFF' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Cargando datos del usuario y plantilla...
            </Typography>
          </Stack>
        </Box>
      </div>
    );
  }

  if (!plantilla) {
    return (
      <div className='p-4 shadow-4 rounded-3 mt-15' style={{ backgroundColor: '#FFFF' }}>
        <Typography>Plantilla no encontrada</Typography>
      </div>
    );
  }

  const PREVIEW_SCALE_FACTOR = 0.65;
  const datosActuales = getDatosActuales();
  const esVigente = plantilla.vigente;

  const tituloSize = (plantilla.tamano_fuente_titulo || 28) * PREVIEW_SCALE_FACTOR;
  const textoSize = (plantilla.tamano_fuente_texto || 14) * PREVIEW_SCALE_FACTOR;

  return (
    <>
      <div className='p-4 shadow-4 rounded-3 mt-15' style={{ backgroundColor: '#FFFF' }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <BackButton />
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h4">Vista Previa del Certificado</Typography>
              {!esVigente && (
                <Chip 
                  label="Plantilla Histórica (Solo lectura)" 
                  size="small" 
                  variant="outlined"
                  color="warning"
                  icon={<LockIcon />}
                />
              )}
              {esVigente && (
                <Chip 
                  label="Plantilla Vigente" 
                  size="small" 
                  color="success"
                  icon={<VisibilityIcon />}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {plantilla.nombre_plantilla}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownloadSample}
            disabled={downloading || !userData}
          >
            {downloading ? 'Descargando...' : 'Descargar Muestra'}
          </Button>
        </Box>

        {/* Sección de usuario actual */}
        {userData && (
          <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    bgcolor: 'primary.main',
                    fontSize: '20px',
                    fontWeight: 'bold'
                  }}
                >
                  {getInitials(datosActuales.nombreCompleto)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Datos del Usuario Actual
                    {userData.role && (
                      <Chip 
                        label={getRoleName(userData.role)}
                        size="small"
                        color="secondary"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <Chip 
                          icon={<PersonIcon />} 
                          label={datosActuales.nombreCompleto}
                          variant="outlined"
                          size="small"
                          title={`ID: ${userData.id}`}
                        />
                        <Chip 
                          icon={<EmailIcon />} 
                          label={formatEmail(userData.email)}
                          variant="outlined"
                          size="small"
                          title={userData.email}
                        />
                        <Chip 
                          icon={<BadgeIcon />} 
                          label={`Doc: ${datosActuales.numeroDocumento}`}
                          variant="outlined"
                          size="small"
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <Chip 
                          icon={<SchoolIcon />} 
                          label={`Curso: ${datosActuales.nombreCurso}`}
                          variant="outlined"
                          size="small"
                          color="primary"
                        />
                        <Chip 
                          icon={<AccessTimeIcon />} 
                          label={`${datosActuales.intensidadHoraria} horas`}
                          variant="outlined"
                          size="small"
                          color="primary"
                        />
                        <Chip 
                          icon={<LocationOnIcon />} 
                          label={datosActuales.ciudad}
                          variant="outlined"
                          size="small"
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Los datos mostrados en el certificado corresponden al usuario actual de la aplicación
                    {userData.userComplemento && ' (datos reales del sistema)'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Preview del Certificado */}
        <Card
          sx={{
            mb: 3,
            backgroundImage: plantilla.fondo_base64 ? `url(${plantilla.fondo_base64})` : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '700px',
            position: 'relative',
            border: '2px solid #e0e0e0',
          }}
        >
          <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', minHeight: '700px' }}>
            
            {plantilla.entidad && (
              <Typography
                variant="h6"
                align="center"
                sx={{
                  fontWeight: 'bold',
                  fontSize: `${tituloSize * 2.0}px`,
                  lineHeight: 1.4,
                  mb: 2,
                  textTransform: 'uppercase',
                  color: plantilla.color_primario || '#000',
                }}
              >
                {plantilla.entidad}
              </Typography>
            )}

            {plantilla.texto_certifican && (
              <Typography
                variant="body1"
                align="center"
                sx={{
                  fontSize: `${textoSize * 2.0}px`,
                  mb: 1.5,
                  color: plantilla.color_primario || '#000',
                }}
              >
                {plantilla.texto_certifican}
              </Typography>
            )}

            <Typography
              variant="h6"
              align="center"
              sx={{
                fontWeight: 'bold',
                fontSize: `${textoSize * 2.0}px`,
                mb: 1,
                color: plantilla.color_primario || '#000',
              }}
            >
              El(la) señor(a) {datosActuales.nombreCompleto}
            </Typography>

            <Typography
              variant="body2"
              align="center"
              sx={{
                fontSize: `${textoSize * 2.0}px`,
                mb: 2,
                color: plantilla.color_primario || '#000',
              }}
            >
              Cédula de ciudadanía {datosActuales.numeroDocumento}
            </Typography>

            {plantilla.titulo_certificado && (
              <Typography
                variant="h4"
                align="center"
                sx={{
                  color: plantilla.color_primario || '#003876',
                  fontWeight: 'bold',
                  mb: 2.5,
                  fontSize: `${textoSize * 2.0}px`,
                  textTransform: 'uppercase',
                }}
              >
                {plantilla.titulo_certificado}
              </Typography>
            )}

            <Box sx={{ px: 4, mb: 2, textAlign: 'center' }}>
              <Typography
                variant="body1"
                sx={{ 
                  fontSize: `${textoSize * 2.0}px`,
                  lineHeight: 1.6,
                  color: plantilla.color_primario || '#000',
                  whiteSpace: 'pre-line',
                }}
                dangerouslySetInnerHTML={{
                  __html: replaceVariables(plantilla.texto_certificado),
                }}
              />
            </Box>

            {plantilla.texto_intensidad_horaria && (
              <Box sx={{ px: 4, mb: 2, textAlign: 'center' }}>
                <Typography
                  variant="body1"
                  sx={{ 
                    fontSize: `${textoSize * 2.0}px`,
                    lineHeight: 1.6,
                    color: plantilla.color_primario || '#000',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: replaceVariables(plantilla.texto_intensidad_horaria),
                  }}
                />
              </Box>
            )}

            {plantilla.texto_contexto && (
              <Box sx={{ px: 6, mb: 2, textAlign: 'justify' }}>
                <Typography
                  variant="body2"
                  sx={{ 
                    fontSize: `${textoSize * 2.0}px`,
                    lineHeight: 1.5,
                    color: plantilla.color_secundario || '#333',
                  }}
                >
                  {plantilla.texto_contexto}
                </Typography>
              </Box>
            )}

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography
                variant="body2"
                sx={{ 
                  fontSize: `${textoSize * 2.0}px`,
                  color: plantilla.color_primario || '#000',
                }}
              >
                Se expide en la ciudad de {datosActuales.ciudad} a los {datosActuales.dia} del mes {datosActuales.mes} del año {datosActuales.anio}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, mt: 'auto' }}>
              <Box sx={{ textAlign: 'center', minWidth: '250px' }}>
                {plantilla.firma_base64 && (
                  <Box sx={{ mb: 1 }}>
                    <img
                      src={plantilla.firma_base64}
                      alt="Firma"
                      style={{ height: '40px', objectFit: 'contain' }}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    borderTop: `2px solid ${plantilla.color_primario || '#000'}`,
                    pt: 1,
                    mt: 1,
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 'bold', 
                      fontSize: `${textoSize * 2.0}px`,
                      color: plantilla.color_primario || '#000',
                    }}
                  >
                    {plantilla.nombre_firmante}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: `${textoSize * 2.0}px`,
                      color: plantilla.color_primario || '#000',
                    }}
                  >
                    {plantilla.cargo_firmante}
                  </Typography>
                  {plantilla.area_firmante && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: `${textoSize * 2.0}px`,
                        color: plantilla.color_secundario || '#666',
                      }}
                    >
                      {plantilla.area_firmante}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {plantilla.logos_base64 && plantilla.logos_base64.length > 0 && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  pt: 2,
                }}
              >
                {plantilla.logos_base64.map((logoBase64, index) => (
                  <Box key={index}>
                    <img
                      src={logoBase64}
                      alt={`Logo ${index + 1}`}
                      style={{ 
                        height: '50px', 
                        objectFit: 'contain',
                        maxWidth: '130px',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Botones de acción - Editar SOLO habilitado para plantilla vigente */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/admin/certificado-plantilla')}>
            Volver
          </Button>
          
          {esVigente ? (
            <Tooltip title="Editar esta plantilla vigente">
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/admin/certificado-plantilla/editar/${id}`)}
              >
                Editar Plantilla
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Las plantillas históricas no se pueden editar. Solo la plantilla vigente es editable.">
              <span>
                <Button
                  variant="contained"
                  startIcon={<LockIcon />}
                  disabled
                  sx={{ opacity: 0.6 }}
                >
                  Editar Plantilla (No disponible)
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          variant={'filled'}
          severity={alert ? 'success' : 'error'}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {alert ? <AlertTitle>Éxito</AlertTitle> : <AlertTitle>Error</AlertTitle>}
          {alertMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

const AdminCertificadoPlantillaPreviewWrapper = () => {
  return (
    <>
      <PageTitle breadcrumbs={[]}>Vista Previa de Plantilla</PageTitle>
      <CertificadoPlantillaPreviewPage />
    </>
  );
};

export { AdminCertificadoPlantillaPreviewWrapper };
export default AdminCertificadoPlantillaPreviewWrapper;